import {
  AddOutlined as AddOutlinedIcon,
  ArrowBackOutlined as ArrowBackOutlinedIcon,
  CalendarMonthOutlined as CalendarMonthOutlinedIcon,
  DeleteOutlineOutlined as DeleteOutlineOutlinedIcon,
  EditOutlined as EditOutlinedIcon,
  PersonAddAltOutlined as PersonAddAltOutlinedIcon,
  VisibilityOutlined as VisibilityOutlinedIcon
} from '@mui/icons-material'
import { Fragment } from 'react'
import { formatCLP } from '../../../Facturacion/utils/formatCLP'
import ResizableModal from '../../../../../ui/ResizableModal'
import SalesNotePreviewDocument from './SalesNotePreviewDocument'

const salesNoteInputClassName =
  'h-[42px] w-full rounded-[10px] border border-slate-200 bg-white px-3 text-sm text-slate-700 outline-none transition focus:border-[#82cfca] focus:ring-2 focus:ring-[#82cfca]/20'

const salesNoteTextareaClassName =
  'w-full rounded-[12px] border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 outline-none transition focus:border-[#82cfca] focus:ring-2 focus:ring-[#82cfca]/20'

const normalizeCurrencyInput = value => {
  const digits = String(value || '').replace(/[^\d]/g, '')
  if (!digits) return ''

  return String(Number(digits))
}

const splitRut = rutString => {
  const normalizedRut = String(rutString || '').trim().replace(/\./g, '').replace(/\s+/g, '').toUpperCase()
  const hasDash = normalizedRut.includes('-')
  const cleanRut = normalizedRut.replace(/-/g, '')

  if (!cleanRut) return { body: '', dv: '' }
  if (!hasDash) {
    return {
      body: cleanRut.replace(/\D/g, ''),
      dv: ''
    }
  }

  return {
    body: cleanRut.slice(0, -1).replace(/\D/g, ''),
    dv: cleanRut.slice(-1).replace(/[^0-9K]/g, '')
  }
}

const buildRut = (body, dv) => {
  const cleanBody = String(body || '').replace(/\D/g, '')
  const cleanDv = String(dv || '')
    .toUpperCase()
    .replace(/[^0-9K]/g, '')
    .slice(0, 1)

  if (!cleanBody && !cleanDv) return ''
  if (!cleanDv) return cleanBody

  return `${cleanBody}-${cleanDv}`
}

const SalesNoteField = ({ label, hint, children, className = '' }) => (
  <div className={className}>
    <label className='mb-1.5 block text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-500'>
      {label}
    </label>
    {children}
    {hint ? <p className='mt-1 text-[10px] text-slate-400'>{hint}</p> : null}
  </div>
)

const ExemptToggle = ({ checked, onChange }) => (
  <button
    type='button'
    onClick={() => onChange(!checked)}
    className={`inline-flex h-7 w-12 items-center rounded-full border px-1 transition ${
      checked
        ? 'border-[#82cfca] bg-[#82cfca]'
        : 'border-slate-300 bg-slate-200'
    }`}
    aria-pressed={checked}
    aria-label='Alternar nota exenta'
  >
    <span
      className={`h-5 w-5 rounded-full bg-white shadow-sm transition ${
        checked ? 'translate-x-5' : 'translate-x-0'
      }`}
    />
  </button>
)

const SalesNoteTotalsSummary = ({ totals, isExempt, onIsExemptChange }) => (
  <div className='rounded-[16px] border border-[#d7ebea] bg-white px-5 py-4'>
    <div className='space-y-3 text-sm text-slate-700'>
      <div className='flex items-center justify-between'>
        <span>Sub-Total</span>
        <span>{formatCLP(totals?.net || 0)}</span>
      </div>
      <div className='flex items-center justify-between gap-3 rounded-[10px] border border-slate-200 bg-slate-50 px-3 py-2'>
        <div>
          <span className='font-medium text-slate-700'>Nota exenta</span>
          <p className='text-[11px] text-slate-500'>Si está activa, el documento no calcula IVA.</p>
        </div>
        <ExemptToggle checked={isExempt} onChange={onIsExemptChange} />
      </div>
      <div className='flex items-center justify-between'>
        <span>IVA (19%)</span>
        <span>{isExempt ? 'No aplica' : formatCLP(totals?.iva || 0)}</span>
      </div>
      <div className='flex items-center justify-between border-t border-slate-200 pt-3 text-base font-semibold text-slate-900'>
        <span>Total</span>
        <span>{formatCLP(totals?.total || 0)}</span>
      </div>
    </div>
  </div>
)

const SalesNoteEditorModal = ({
  open,
  view,
  currentBusiness,
  company,
  draft,
  totals,
  itemOptions,
  isCustomerRutLoading,
  onClose,
  onPreview,
  onSaveDraft,
  onStartNewNote,
  onShowForm,
  onCustomerChange,
  onSearchCustomerByRut,
  onDateIssuedChange,
  onDueDateChange,
  onIsExemptChange,
  onReferenceChange,
  onNoteChange,
  onLineChange,
  onLineSourceTypeChange,
  onInventoryItemApply,
  onAddLine,
  onRemoveLine
}) => {
  const isFormView = view === 'form'
  const customerRut = splitRut(draft.customer.rut)

  const handleCustomerRutBodyChange = event => {
    const nextBody = event.target.value.replace(/\D/g, '').slice(0, 8)
    onCustomerChange('rut', buildRut(nextBody, customerRut.dv))
  }

  const handleCustomerRutDvChange = event => {
    const nextDv = event.target.value
      .toUpperCase()
      .replace(/[^0-9K]/g, '')
      .slice(0, 1)

    onCustomerChange('rut', buildRut(customerRut.body, nextDv))
  }

  const handleCustomerRutBlur = () => {
    const nextRut = buildRut(customerRut.body, customerRut.dv)

    onCustomerChange('rut', nextRut)

    if (customerRut.body && customerRut.dv) {
      onSearchCustomerByRut(nextRut)
    }
  }

  return (
    <ResizableModal
      open={open}
      onClose={onClose}
      width={1220}
      height={860}
      minWidth={980}
      minHeight={680}
      size='large'
    >
      {isFormView ? (
        <div className='mx-auto max-w-[1180px] space-y-4 bg-[#f4f7fb] p-2'>
          <div className='overflow-hidden rounded-[20px] border border-[#d5eceb] bg-white shadow-[0_14px_36px_rgba(15,23,42,0.08)]'>
            <div className='bg-[#82cfca] px-4 py-2 text-[11px] font-medium text-white'>
              Dirección de Empresa, Datos de Contacto, Título y Logo
            </div>

            <div className='grid gap-5 px-4 py-4 lg:grid-cols-[minmax(0,1fr)_300px]'>
              <div className='min-h-[142px] rounded-[14px] border border-dashed border-[#82cfca] bg-[#fbfefe] px-5 py-4'>
                <p className='text-[10px] font-semibold uppercase tracking-[0.14em] text-[#63bdb8]'>
                  Empresa emisora
                </p>
                <h3 className='mt-4 text-2xl font-semibold text-slate-700'>{company.label}</h3>
                <div className='mt-4 space-y-1.5 text-sm text-slate-500'>
                  {company.rut ? <p>{company.rut}</p> : null}
                  {company.address ? <p>{company.address}</p> : null}
                  {company.district || company.city ? (
                    <p>{[company.district, company.city].filter(Boolean).join(', ')}</p>
                  ) : null}
                </div>
              </div>

              <div className='flex flex-col items-end justify-between gap-8 text-right'>
                <div className='pt-2 text-[20px] leading-none text-slate-700 md:text-[22px]'>
                  <span className='font-light'>Nota de Venta</span>
                  <span className='font-light text-slate-500'>
                    #{String(draft.internalCode).replace('NV-', '')}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className='overflow-hidden rounded-[20px] border border-[#9ed9d7] bg-white shadow-[0_14px_36px_rgba(15,23,42,0.08)]'>
            <div className='grid gap-4 p-4 xl:grid-cols-[132px_minmax(0,1fr)_230px]'>
              <section className='rounded-[14px] border border-slate-200 bg-white px-3 py-4 text-center shadow-[0_4px_16px_rgba(15,23,42,0.08)]'>
                <div className='mx-auto flex h-14 w-14 items-center justify-center rounded-full border border-slate-200 text-slate-400'>
                  <PersonAddAltOutlinedIcon style={{ fontSize: '1.85rem' }} />
                </div>
                <p className='mt-4 text-[11px] font-medium leading-4 text-slate-600'>
                  Seleccionar o Crear Cliente
                </p>
              </section>

              <section className='grid gap-3 md:grid-cols-2'>
                <SalesNoteField label='Cliente'>
                  <input
                    value={draft.customer.businessName}
                    onChange={event => onCustomerChange('businessName', event.target.value)}
                    className={salesNoteInputClassName}
                    placeholder='Razón social o nombre comercial'
                  />
                </SalesNoteField>

                <SalesNoteField label='RUT'>
                  <div>
                    <div className='grid grid-cols-[minmax(0,1fr)_40px_72px] gap-2'>
                      <input
                        value={customerRut.body}
                        onChange={handleCustomerRutBodyChange}
                        className={salesNoteInputClassName}
                        inputMode='numeric'
                        maxLength={8}
                        placeholder='1234'
                      />

                      <div className='inline-flex h-[42px] items-center justify-center rounded-[10px] border border-slate-200 bg-slate-50 text-sm font-semibold text-slate-500'>
                        -
                      </div>

                      <input
                        value={customerRut.dv}
                        onChange={handleCustomerRutDvChange}
                        onBlur={handleCustomerRutBlur}
                        className={`${salesNoteInputClassName} text-center uppercase`}
                        maxLength={1}
                        placeholder='3'
                      />
                    </div>
                    {isCustomerRutLoading ? (
                      <p className='mt-1 text-[10px] text-[#5caeaa]'>Buscando cliente por RUT...</p>
                    ) : (
                      <p className='mt-1 text-[10px] text-slate-400'>
                        La validación se hace cuando sales del campo del dígito verificador.
                      </p>
                    )}
                  </div>
                </SalesNoteField>

                <SalesNoteField label='Contacto'>
                  <input
                    value={draft.customer.contact}
                    onChange={event => onCustomerChange('contact', event.target.value)}
                    className={salesNoteInputClassName}
                    placeholder='Persona, correo o teléfono'
                  />
                </SalesNoteField>

                <SalesNoteField label='Dirección'>
                  <input
                    value={draft.customer.address}
                    onChange={event => onCustomerChange('address', event.target.value)}
                    className={salesNoteInputClassName}
                    placeholder='Dirección comercial'
                  />
                </SalesNoteField>

                <SalesNoteField label='Comuna'>
                  <input
                    value={draft.customer.district}
                    onChange={event => onCustomerChange('district', event.target.value)}
                    className={salesNoteInputClassName}
                    placeholder='Comuna'
                  />
                </SalesNoteField>

                <SalesNoteField label='Ciudad'>
                  <input
                    value={draft.customer.city}
                    onChange={event => onCustomerChange('city', event.target.value)}
                    className={salesNoteInputClassName}
                    placeholder='Ciudad'
                  />
                </SalesNoteField>
              </section>

              <section className='space-y-3'>
                <SalesNoteField label='Fecha de Emisión'>
                  <div className='relative'>
                    <input
                      type='date'
                      value={draft.dateIssued}
                      onChange={event => onDateIssuedChange(event.target.value)}
                      className={`${salesNoteInputClassName} pr-10`}
                    />
                    <CalendarMonthOutlinedIcon
                      className='pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400'
                      style={{ fontSize: '1rem' }}
                    />
                  </div>
                </SalesNoteField>

                <SalesNoteField
                  label='Fecha de Vencimiento'
                  hint='Puedes usar la misma fecha si no aplica crédito.'
                >
                  <div className='relative'>
                    <input
                      type='date'
                      value={draft.dueDate}
                      onChange={event => onDueDateChange(event.target.value)}
                      className={`${salesNoteInputClassName} pr-10`}
                    />
                    <CalendarMonthOutlinedIcon
                      className='pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400'
                      style={{ fontSize: '1rem' }}
                    />
                  </div>
                </SalesNoteField>
              </section>
            </div>

            <div className='border-t border-[#d9eceb]'>
              <div className='overflow-x-auto'>
                <table className='min-w-full text-sm'>
                  <thead className='bg-[#f3f5f6] text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-500'>
                    <tr>
                      <th className='w-[168px] min-w-[168px] px-4 py-3 text-left'>Tipo</th>
                      <th className='min-w-[420px] px-4 py-3 text-left'>Producto</th>
                      <th className='w-[152px] min-w-[152px] px-4 py-3 text-left'>Cantidad</th>
                      <th className='w-[138px] min-w-[138px] px-4 py-3 text-left'>Precio</th>
                      <th className='w-[120px] min-w-[120px] px-4 py-3 text-left'>Descuento</th>
                      <th className='w-[144px] min-w-[144px] px-4 py-3 text-right'>Monto</th>
                      <th className='w-[88px] min-w-[88px] px-4 py-3 text-center'>Acciones</th>
                    </tr>
                  </thead>

                  <tbody>
                    {draft.lines.map((line, index) => (
                      <Fragment key={`sales-note-line-${index}`}>
                        <tr className='border-t border-[#e8ecef] align-top'>
                          <td className='w-[168px] min-w-[168px] px-4 py-4'>
                            <select
                              value={line.sourceType}
                              onChange={event => onLineSourceTypeChange(index, event.target.value)}
                              className={`${salesNoteInputClassName} min-w-[150px]`}
                            >
                              <option value='manual'>Manual</option>
                              <option value='catalog'>Item catálogo</option>
                            </select>
                          </td>

                          <td className='min-w-[420px] px-4 py-4'>
                            <div className='space-y-2'>
                              {line.sourceType === 'catalog' ? (
                                <select
                                  value={line.inventoryItemId}
                                  onChange={event => onInventoryItemApply(index, event.target.value)}
                                  className={salesNoteInputClassName}
                                >
                                  <option value=''>Seleccionar item</option>
                                  {itemOptions.map(option => (
                                    <option key={option.id} value={option.id}>
                                      {option.label}
                                    </option>
                                  ))}
                                </select>
                              ) : (
                                <input
                                  value={line.itemName}
                                  onChange={event => onLineChange(index, 'itemName', event.target.value)}
                                  className={salesNoteInputClassName}
                                  placeholder='Nombre del producto o servicio'
                                />
                              )}

                              <div className='grid gap-2 md:grid-cols-[140px_minmax(0,1fr)_148px]'>
                                <input
                                  value={line.itemCode}
                                  onChange={event => onLineChange(index, 'itemCode', event.target.value)}
                                  className={salesNoteInputClassName}
                                  placeholder='Código'
                                />
                                <input
                                  value={line.itemDescription}
                                  onChange={event =>
                                    onLineChange(index, 'itemDescription', event.target.value)
                                  }
                                  className={salesNoteInputClassName}
                                  placeholder='Descripción breve'
                                />
                                <label className='inline-flex h-[42px] items-center justify-center gap-2 rounded-[10px] border border-slate-200 bg-slate-50 px-3 text-xs font-medium text-slate-600'>
                                  <input
                                    type='checkbox'
                                    checked={line.showDescription}
                                    onChange={event =>
                                      onLineChange(index, 'showDescription', event.target.checked)
                                    }
                                  />
                                  Descripción extra
                                </label>
                              </div>

                              {line.showDescription ? (
                                <textarea
                                  value={line.itemDescription}
                                  onChange={event =>
                                    onLineChange(index, 'itemDescription', event.target.value)
                                  }
                                  className={`${salesNoteTextareaClassName} min-h-[76px]`}
                                  placeholder='Detalle extendido del producto o servicio'
                                />
                              ) : null}
                            </div>
                          </td>

                          <td className='w-[152px] min-w-[152px] px-4 py-4'>
                            <div className='space-y-2'>
                              <input
                                type='number'
                                min='0'
                                step='0.0001'
                                value={line.quantity}
                                onChange={event => onLineChange(index, 'quantity', event.target.value)}
                                className={`${salesNoteInputClassName} text-right tabular-nums`}
                              />
                              <input
                                value={line.unit}
                                onChange={event => onLineChange(index, 'unit', event.target.value)}
                                className={`${salesNoteInputClassName} text-center`}
                                placeholder='UN'
                              />
                            </div>
                          </td>

                          <td className='w-[138px] min-w-[138px] px-4 py-4'>
                            <input
                              type='text'
                              inputMode='numeric'
                              value={line.unitPrice === '' ? '' : formatCLP(line.unitPrice)}
                              onChange={event =>
                                onLineChange(
                                  index,
                                  'unitPrice',
                                  normalizeCurrencyInput(event.target.value)
                                )
                              }
                              className={`${salesNoteInputClassName} text-right tabular-nums`}
                              placeholder='$ 0'
                            />
                          </td>

                          <td className='w-[120px] min-w-[120px] px-4 py-4'>
                            <input
                              type='number'
                              min='0'
                              max='100'
                              step='0.01'
                              value={line.discountPercent}
                              onChange={event =>
                                onLineChange(index, 'discountPercent', event.target.value)
                              }
                              className={`${salesNoteInputClassName} text-right tabular-nums`}
                              placeholder='0'
                            />
                          </td>

                          <td className='w-[144px] min-w-[144px] px-4 py-4 text-right'>
                            <div className='rounded-[10px] border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm font-semibold text-slate-700 tabular-nums'>
                              {formatCLP(line.subtotal)}
                            </div>
                          </td>

                          <td className='w-[88px] min-w-[88px] px-4 py-4 text-center'>
                            <button
                              type='button'
                              onClick={() => onRemoveLine(index)}
                              className='inline-flex h-10 w-10 items-center justify-center rounded-full border border-red-200 text-red-500 transition hover:bg-red-50'
                              aria-label='Eliminar línea'
                            >
                              <DeleteOutlineOutlinedIcon style={{ fontSize: '1rem' }} />
                            </button>
                          </td>
                        </tr>
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className='flex justify-center border-t border-[#e8ecef] px-4 py-5'>
                <button
                  type='button'
                  onClick={onAddLine}
                  className='inline-flex items-center justify-center gap-1.5 rounded-[8px] bg-[#82cfca] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#6dc0bb]'
                >
                  <AddOutlinedIcon style={{ fontSize: '1rem' }} />
                  Agregar item
                </button>
              </div>
            </div>

            <div className='grid gap-4 border-t border-[#d9eceb] p-4 lg:grid-cols-[minmax(0,1fr)_270px]'>
              <section className='space-y-4'>

                <SalesNoteField label='Notas'>
                  <textarea
                    value={draft.note}
                    onChange={event => onNoteChange(event.target.value)}
                    className={`${salesNoteTextareaClassName} min-h-[120px]`}
                    placeholder='Deja una nota aquí...'
                  />
                </SalesNoteField>
              </section>

              <SalesNoteTotalsSummary
                totals={totals}
                isExempt={Boolean(draft.isExempt)}
                onIsExemptChange={onIsExemptChange}
              />
            </div>
          </div>

          <section className='flex flex-col gap-3 md:flex-row md:items-center md:justify-end'>
            <button
              type='button'
              onClick={onClose}
              className='inline-flex items-center justify-center gap-1.5 rounded-[8px] border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:border-slate-400 hover:bg-slate-50'
            >
              <ArrowBackOutlinedIcon style={{ fontSize: '1rem' }} />
              Cancelar
            </button>

            <button
              type='button'
              onClick={onPreview}
              className='inline-flex items-center justify-center gap-1.5 rounded-[8px] border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:border-slate-400 hover:bg-slate-50'
            >
              <VisibilityOutlinedIcon style={{ fontSize: '1rem' }} />
              Vista previa
            </button>

            <button
              type='button'
              onClick={onSaveDraft}
              className='inline-flex items-center justify-center gap-1.5 rounded-[8px] bg-[#82cfca] px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-[#6dc0bb]'
            >
              Guardar y continuar
            </button>
          </section>
        </div>
      ) : (
        <div className='space-y-4'>
          <div className='flex flex-col gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 md:flex-row md:items-center md:justify-between'>
            <div>
              <p className='text-xs font-semibold uppercase tracking-[0.14em] text-slate-500'>
                Previsualización
              </p>
              <p className='mt-1 text-sm text-slate-600'>
                Esta vista replica el formato interno de la nota de venta. Puedes volver a editar o
                guardar el borrador para dejarlo en el listado.
              </p>
            </div>

            <div className='flex flex-wrap items-center gap-2'>
              <div className='rounded-full border border-slate-300 bg-white px-3 py-1 text-xs font-semibold uppercase tracking-[0.14em] text-slate-600'>
                {draft.internalCode}
              </div>
              <button
                type='button'
                onClick={onClose}
                className='inline-flex items-center justify-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition hover:border-slate-400 hover:bg-slate-50'
              >
                <ArrowBackOutlinedIcon style={{ fontSize: '1rem' }} />
                Volver al listado
              </button>
            </div>
          </div>

          <div className='overflow-auto rounded-2xl border border-slate-200 bg-white p-4 shadow-sm'>
            <SalesNotePreviewDocument currentBusiness={currentBusiness} note={draft} totals={totals} />
          </div>

          <section className='flex flex-col gap-3 md:flex-row md:items-center md:justify-center'>
            <button
              type='button'
              onClick={onShowForm}
              className='inline-flex items-center justify-center gap-1.5 rounded-lg bg-[#f26c22] px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-[#d95e1f]'
            >
              <EditOutlinedIcon style={{ fontSize: '1rem' }} />
              Volver a editar
            </button>

            <button
              type='button'
              onClick={onSaveDraft}
              className='inline-flex items-center justify-center gap-1.5 rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:border-slate-400 hover:bg-slate-50'
            >
              Guardar borrador
            </button>

            <button
              type='button'
              onClick={onStartNewNote}
              className='inline-flex items-center justify-center gap-1.5 rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:border-slate-400 hover:bg-slate-50'
            >
              Nueva nota
            </button>
          </section>
        </div>
      )}
    </ResizableModal>
  )
}

export default SalesNoteEditorModal
